import { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  cn,
  Typography,
} from "@cointervue/design-system";
import { Check, Copy, Info, Laptop, Maximize2 } from "lucide-react";
import type { RealDeviceType } from "./useCandidateDeviceCheck";

export interface LaptopRequiredCardProps {
  className?: string;
  interviewUrl?: string;
  deviceType?: RealDeviceType;
  isWindowTooSmall?: boolean;
}

export function LaptopRequiredCard({
  className,
  interviewUrl,
  deviceType = "mobile",
  isWindowTooSmall = false,
}: LaptopRequiredCardProps) {
  const [isCopied, setIsCopied] = useState(false);

  const isDesktopNarrow = deviceType === "desktop" && isWindowTooSmall;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(interviewUrl || window.location.href);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy interview URL:", err);
    }
  };

  const getTitle = () => {
    if (isDesktopNarrow) {
      return "Maximize Your Browser Window";
    }
    return "Laptop or Desktop Required";
  };

  const getDescription = () => {
    if (isDesktopNarrow) {
      return "Your browser window is currently too small for the interview workspace. Please maximize or expand your browser window to at least 1024px width to proceed.";
    }
    if (deviceType === "tablet") {
      return "You have joined from a tablet device. Candidate interviews cannot be taken on tablets. Please open this interview link on a laptop or desktop computer to proceed.";
    }
    return "You have joined from a mobile phone. Candidate interviews cannot be taken on mobile phones. Please open this interview link on a laptop or desktop computer to proceed.";
  };

  const guidanceSteps = isDesktopNarrow
    ? [
        "The interview interface requires a minimum display width of 1024px.",
        "Expand or maximize this browser window to full screen.",
        "Ensure your zoom level is set to 100% for the best experience.",
      ]
    : [
        "Screen sharing, code editor, and interactive tools require a desktop environment.",
        "Webcam and audio proctoring are optimized for laptop and desktop webcams.",
        "Supported browsers: Google Chrome, Microsoft Edge, or Mozilla Firefox.",
      ];

  return (
    <Card
      className={cn(
        "w-full max-w-md rounded-2xl shadow-lg border border-brand-gray-light bg-white",
        className,
      )}
    >
      <CardHeader className="text-center space-y-2 p-5 pb-3">
        <div className="flex justify-center mb-1">
          <div className="h-14 w-14 rounded-full flex items-center justify-center bg-blue-50 border border-blue-100 text-brand-blue shadow-xs">
            {isDesktopNarrow ? (
              <Maximize2 className="h-7 w-7 text-brand-blue" />
            ) : (
              <Laptop className="h-7 w-7 text-brand-blue" />
            )}
          </div>
        </div>

        <CardTitle className="text-xl font-bold text-brand-black">
          {getTitle()}
        </CardTitle>

        <CardDescription className="text-xs sm:text-sm text-brand-black-2 font-normal leading-relaxed">
          {getDescription()}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3 p-5 pt-1">
        <div className="rounded-2xl bg-brand-gray-light/40 p-4 text-left">
          <div className="flex items-center gap-2 mb-2">
            <Info className="h-4 w-4 text-brand-blue shrink-0" />
            <Typography
              variant="bodySmall"
              weight="semibold"
              className="text-brand-black"
            >
              {isDesktopNarrow ? "How to continue:" : "Why is a laptop required?"}
            </Typography>
          </div>

          <ul className="list-disc pl-5 space-y-1.5 marker:text-brand-blue text-xs sm:text-sm">
            {guidanceSteps.map((step, index) => (
              <li key={index}>
                <Typography variant="bodySmall">{step}</Typography>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>

      <CardFooter className="flex-col gap-2 p-5 pt-0">
        <Button
          variant="interviewPrimaryAction"
          className="w-full flex items-center justify-center gap-2"
          onClick={handleCopyLink}
        >
          {isCopied ? (
            <>
              <Check className="w-4 h-4 text-white" />
              <span>Interview Link Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4 text-white" />
              <span>Copy Interview Link</span>
            </>
          )}
        </Button>

        <Typography
          variant="caption"
          className="text-muted-foreground text-center"
        >
          {isDesktopNarrow
            ? "Maximize your window to proceed immediately."
            : "Copy this link and open it in your computer's browser to continue."}
        </Typography>
      </CardFooter>
    </Card>
  );
}
